/**
 * Captura da tela visível para anexar ao feedback, com campos sensíveis
 * mascarados. Só roda quando o usuário marca "Anexar captura da tela".
 *
 * Mascaramento (desenhado por cima da imagem, no canvas — o DOM real não é
 * alterado): todo input/textarea/select, todo elemento com [data-sensitive]
 * (chat, documentos, contato, responsável legal) e os modais abertos
 * (inclusive o próprio diálogo de feedback) são excluídos da renderização.
 */
import { toCanvas } from 'html-to-image';
import { FEEDBACK_CAPTURA_MAX_BYTES } from '@patrociconnect/shared';

const SENSITIVE_SELECTOR = 'input, textarea, select, [data-sensitive]';

function collectExcludedRoots(): Set<Element> {
  const excluded = new Set<Element>();
  document.querySelectorAll('[aria-modal="true"]').forEach((dialog) => {
    // O wrapper fixo do Modal contém também o fundo escurecido
    excluded.add(dialog.parentElement ?? dialog);
  });
  const floating = document.getElementById('floating-actions');
  if (floating) excluded.add(floating);
  return excluded;
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

export async function captureMaskedScreen(): Promise<Blob> {
  const body = document.body;
  const excluded = collectExcludedRoots();
  const bodyRect = body.getBoundingClientRect();

  const fullCanvas = await toCanvas(body, {
    pixelRatio: 1,
    cacheBust: true,
    // Imagens de outros domínios sem CORS viram lacunas em vez de quebrar a captura
    imagePlaceholder: 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==',
    filter: (node) => !(node instanceof Element && excluded.has(node)),
  });

  const width = window.innerWidth;
  const height = window.innerHeight;
  const output = document.createElement('canvas');
  output.width = width;
  output.height = height;
  const context = output.getContext('2d');
  if (!context) throw new Error('Canvas indisponível para a captura.');

  // Recorta só a área visível (posição do body em relação à viewport)
  context.fillStyle = getComputedStyle(body).backgroundColor || '#ffffff';
  context.fillRect(0, 0, width, height);
  context.drawImage(fullCanvas, bodyRect.left, bodyRect.top);

  context.font = '600 14px system-ui, sans-serif';
  context.textBaseline = 'middle';
  document.querySelectorAll<HTMLElement>(SENSITIVE_SELECTOR).forEach((element) => {
    if ([...excluded].some((root) => root.contains(element))) return;
    const rect = element.getBoundingClientRect();
    if (rect.width < 1 || rect.height < 1 || rect.bottom < 0 || rect.top > height) return;
    context.fillStyle = '#1f2937';
    context.fillRect(rect.left, rect.top, rect.width, rect.height);
    if (rect.height >= 14 && rect.width >= 40) {
      context.fillStyle = '#e5e7eb';
      context.fillText('••••', rect.left + 8, rect.top + Math.min(rect.height / 2, 16));
    }
  });

  const png = await canvasToBlob(output, 'image/png');
  if (png && png.size <= FEEDBACK_CAPTURA_MAX_BYTES) return png;
  const jpeg = await canvasToBlob(output, 'image/jpeg', 0.75);
  if (jpeg && jpeg.size <= FEEDBACK_CAPTURA_MAX_BYTES) return jpeg;
  throw new Error('A captura ficou grande demais para anexar.');
}
